import {
  DatabaseConfig,
  initializeDatabase,
  addToStore,
  getAllFromStore,
  clearStore,
  deleteFromStore,
  countInStore
} from './indexedDBUtils';
import { IClickstreamEvent } from 'src/service/clickStream.type';

const DB_NAME = 'ClickstreamDB';
const STORE_NAME = 'events';
const DB_VERSION = 1;

interface IClickstreamEventRecord extends IClickstreamEvent {
  id?: number; // auto-increment primary key
}

const dbConfig: DatabaseConfig = {
  dbName: DB_NAME,
  version: DB_VERSION,
  storeName: STORE_NAME,
  keyPath: 'id',
  autoIncrement: true
};

async function getClickstreamDB(): Promise<IDBDatabase> {
  return initializeDatabase(dbConfig);
}

export async function addEventToDB(event: IClickstreamEvent): Promise<void> {
  const db = await getClickstreamDB();

  try {
    await addToStore(db, STORE_NAME, event as IClickstreamEventRecord);
  } catch (error) {
    throw new Error(`Failed to add event to DB: ${error}`);
  } finally {
    db.close();
  }
}

export async function getAllEventsFromDB(): Promise<IClickstreamEvent[]> {
  const db = await getClickstreamDB();

  try {
    const events = await getAllFromStore<IClickstreamEventRecord>(db, STORE_NAME);
    return events as IClickstreamEvent[];
  } catch (error) {
    throw new Error(`Failed to get events from DB: ${error}`);
  } finally {
    db.close();
  }
}

export async function clearEventsFromDB(): Promise<void> {
  const db = await getClickstreamDB();

  try {
    await clearStore(db, STORE_NAME);
  } catch (error) {
    throw new Error(`Failed to clear events from DB: ${error}`);
  } finally {
    db.close();
  }
}

export async function clearSpecificEventsFromDB(eventIds: number[]): Promise<void> {
  const db = await getClickstreamDB();

  try {
    for (const id of eventIds) {
      await deleteFromStore(db, STORE_NAME, id);
    }
  } catch (error) {
    throw new Error(`Failed to clear specific events from DB: ${error}`);
  } finally {
    db.close();
  }
}

export async function getEventsCountFromDB(): Promise<number> {
  const db = await getClickstreamDB();

  try {
    const count = await countInStore(db, STORE_NAME);
    return count;
  } catch (error) {
    throw new Error(`Failed to get events count from DB: ${error}`);
  } finally {
    db.close();
  }
}
